import { safeLocalStorage } from "./safeLocalStorage";

/**
 * Which project a bare entry opens (the app root, or an old link with no `/{company}/{project}`
 * prefix): the one this browser session opened last. The Stytch cookie names the session's
 * organization, and a project URL tab never moves it, so it cannot answer this. One entry, keyed
 * to the source session, so a fresh sign-in opens its own organization and another person on this
 * browser never inherits it. It only chooses which admission to ask for: no token, no authority.
 */
export const LAST_PROJECT_KEY = "pp.lastProject";

const SLUG = /^[a-z0-9][a-z0-9_-]*$/i;

export interface LastProject {
    companySlug: string;
    projectSlug: string;
}

/** The source session a hint belongs to, `organization_id:member_id:member_session_id`. */
export function lastProjectSource(
    organizationId: string | undefined,
    memberId: string | undefined,
    memberSessionId: string | undefined,
): string | null {
    return organizationId && memberId && memberSessionId
        ? `${organizationId}:${memberId}:${memberSessionId}`
        : null;
}

export function readLastProject(source: string): LastProject | null {
    try {
        const stored = JSON.parse(safeLocalStorage.getItem(LAST_PROJECT_KEY) ?? "null") as {
            source?: unknown;
            companySlug?: unknown;
            projectSlug?: unknown;
        } | null;
        const { companySlug, projectSlug } = stored ?? {};
        return stored?.source === source &&
            typeof companySlug === "string" &&
            typeof projectSlug === "string" &&
            SLUG.test(companySlug) &&
            SLUG.test(projectSlug)
            ? { companySlug, projectSlug }
            : null;
    } catch {
        return null;
    }
}

export function rememberLastProject(source: string, project: LastProject): void {
    safeLocalStorage.setItem(LAST_PROJECT_KEY, JSON.stringify({ source, ...project }));
}

export function forgetLastProject(): void {
    safeLocalStorage.removeItem(LAST_PROJECT_KEY);
}
